import { useId, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  DropdownMenuLabel,
  Input,
  InputDecoration,
  InputGroup,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { Book, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type KBOption = { id: string; name: string };

type Props = {
  options: ReadonlyArray<KBOption>;
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
};

export function ChatKnowledgeBaseSelectorV2({
  options,
  value,
  onChange,
  disabled,
  loading,
}: Props) {
  const { t } = useTranslation('chats');

  const [search, setSearch] = useState('');
  const headingId = useId();
  const selectionId = useId();

  const hasSelection = value.length > 0;
  const triggerDisabled = disabled || loading;
  const tooltipLabel = hasSelection
    ? t('chats.chatKnowledgeBaseSelectorV2.knowledgeBasesWithCount', {
        selectedCount: value.length,
      })
    : t('chats.chatKnowledgeBaseSelectorV2.addKnowledge');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  const handleToggle = (id: string) => {
    const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
    onChange(next);
  };

  const handleSelectAll = () => onChange(options.map((o) => o.id));
  const handleClearAll = () => onChange([]);

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) setSearch('');
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="group relative inline-flex items-center">
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={
                  hasSelection ? 'h-8 rounded-lg px-2 gap-1 group-hover:pr-7' : 'h-8 w-8 rounded-lg'
                }
                aria-label={t('chats.chatKnowledgeBaseSelectorV2.addKnowledge')}
                aria-describedby={hasSelection ? selectionId : undefined}
                disabled={triggerDisabled}
              >
                <span className="toolbar-icon-hover">
                  <Book className="h-4 w-4" />
                </span>
                {hasSelection && (
                  <span className="text-xs font-medium text-foreground">{value.length}</span>
                )}
              </Button>
            </PopoverTrigger>
            {hasSelection && (
              <span id={selectionId} hidden>
                {tooltipLabel}
              </span>
            )}
            {hasSelection && !triggerDisabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearAll();
                }}
                aria-label={t('chats.chatKnowledgeBaseSelectorV2.clearAllKnowledgeBases')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center size-4 rounded-sm text-foreground-subtle opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipLabel}</p>
        </TooltipContent>
      </Tooltip>

      <PopoverContent align="start" sideOffset={4} className="p-0 w-80" aria-labelledby={headingId}>
        <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
          <DropdownMenuLabel id={headingId} className="py-1 px-0">
            {t('chats.chatKnowledgeBaseSelectorV2.knowledgeBases')}
          </DropdownMenuLabel>
          <div className="flex items-center gap-1">
            {hasSelection && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleClearAll}
              >
                {t('chats.chatKnowledgeBaseSelectorV2.clear')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleSelectAll}
            >
              {t('chats.chatKnowledgeBaseSelectorV2.selectAll')}
            </Button>
          </div>
        </div>

        <div className="px-3 pb-2">
          <InputGroup
            size="sm"
            className="w-full border-0 has-[input:focus-visible]:ring-0 has-[input:focus-visible]:ring-offset-0"
          >
            <InputDecoration muted>
              <Search className="size-3.5" />
            </InputDecoration>
            <Input
              placeholder={t('chats.chatKnowledgeBaseSelectorV2.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={t('chats.chatKnowledgeBaseSelectorV2.searchKnowledgeBases')}
              className="border-0 bg-transparent"
            />
          </InputGroup>
        </div>

        <div className="max-h-72 overflow-y-auto px-1 pb-2">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-foreground-subtle">
              {t('chats.chatKnowledgeBaseSelectorV2.noResults')}
            </div>
          ) : (
            filtered.map((kb) => {
              const isSelected = value.includes(kb.id);
              return (
                <div
                  key={kb.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleToggle(kb.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggle(kb.id);
                    }
                  }}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                >
                  <Checkbox
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggle(kb.id)}
                    className="data-[state=checked]:bg-foreground data-[state=checked]:border-foreground"
                  />
                  <span className="flex-1 truncate" title={kb.name}>
                    {kb.name}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
